import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  applyApiErrors,
  contactFormSchema,
  contactKeys,
  contactToForm,
  emptyContactForm,
  idempotency,
  KIND,
  toContactRequest,
  type Contact,
  type ContactForm,
  type ContactKind,
} from './model';

const FIELDS = [
  'name',
  'phone',
  'email',
  'address',
  'taxNumber',
  'notes',
  'isCustomer',
  'isSupplier',
  'creditLimit',
  'paymentTermsDays',
] as const satisfies readonly (keyof ContactForm)[];

function useDebounced<T>(value: T, delay = 400): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** Create or edit a customer/supplier. A contact may be both; the other role is a checkbox. */
export function ContactFormDialog({
  open,
  kind,
  contact,
  onClose,
  onSaved,
}: {
  open: boolean;
  kind: ContactKind;
  contact?: Contact | null;
  onClose: () => void;
  onSaved?: (contact: Contact) => void;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { can } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const qc = useQueryClient();
  const editing = !!contact;
  const k = KIND[kind];

  const form = useForm<ContactForm>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: contact ? contactToForm(contact) : emptyContactForm(kind),
    mode: 'onBlur',
  });
  const { register, handleSubmit, control, reset, setError, formState } = form;
  const { errors } = formState;

  useEffect(() => {
    if (open) reset(contact ? contactToForm(contact) : emptyContactForm(kind));
  }, [open, contact, kind, reset]);

  const [name, phone, isCustomer, isSupplier] = useWatch({
    control,
    name: ['name', 'phone', 'isCustomer', 'isSupplier'],
  });
  const dupName = useDebounced(name.trim());
  const dupPhone = useDebounced(phone.trim());
  const duplicates = useQuery({
    queryKey: contactKeys.duplicates({ dupName, dupPhone, id: contact?.id }),
    queryFn: () =>
      unwrap(
        api.GET('/contacts/duplicates', {
          params: {
            query: {
              name: dupName || undefined,
              phone: dupPhone || undefined,
              excludeId: contact?.id,
            },
          },
        }),
      ),
    enabled: open && (dupName.length >= 3 || dupPhone.length >= 6),
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: (values: ContactForm) => {
      const body = toContactRequest(values);
      return contact
        ? unwrap(
            api.PATCH('/contacts/{id}', {
              params: { path: { id: contact.id } },
              body: { ...body, version: contact.version },
            }),
          )
        : unwrap(api.POST('/contacts', { params: { header: idempotency() }, body }));
    },
    onSuccess: async (saved) => {
      notify.success(editing ? `${saved.name} updated` : `${saved.name} added`);
      await qc.invalidateQueries({ queryKey: contactKeys.all });
      onSaved?.(saved);
      onClose();
    },
    onError: (e) => {
      if (!applyApiErrors(e, FIELDS, setError)) notify.error(e);
    },
  });

  const canCustomer = can(editing ? 'customers.update' : 'customers.create');
  const canSupplier = can(editing ? 'suppliers.update' : 'suppliers.create');
  const dupList = duplicates.data?.data ?? [];

  return (
    <Dialog
      open={open}
      onClose={save.isPending ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
    >
      <Box
        component="form"
        noValidate
        onSubmit={(e) => void handleSubmit((v) => save.mutate(v))(e)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
            e.preventDefault();
            void handleSubmit((v) => save.mutate(v))();
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          {editing ? `Edit ${contact.name}` : `New ${k.singular}`}
        </DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
          {editing
            ? 'Changes apply to future documents; posted documents keep their details.'
            : `Add a ${k.singular} to use on ${kind === 'customer' ? 'sales and quotations' : 'purchases and bills'}.`}
        </Typography>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Stack spacing={2.25}>
            <TextField
              label="Name"
              required
              autoFocus
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Phone"
                type="tel"
                {...register('phone')}
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
              <TextField
                label="Email"
                type="email"
                {...register('email')}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Stack>
            {dupList.length > 0 && (
              <Alert severity="warning" variant="outlined">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Possible duplicate{dupList.length > 1 ? 's' : ''}
                </Typography>
                {dupList.slice(0, 3).map((d) => (
                  <Typography key={d.id} variant="body2">
                    {d.name}
                    {d.phone ? ` · ${d.phone}` : ''} —{' '}
                    {d.reason === 'SAME_PHONE' ? 'same phone' : 'similar name'}
                  </Typography>
                ))}
              </Alert>
            )}
            <TextField
              label="Address"
              multiline
              minRows={2}
              {...register('address')}
              error={!!errors.address}
              helperText={errors.address?.message}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Role
              </Typography>
              <Stack direction="row" spacing={2}>
                <Controller
                  control={control}
                  name="isCustomer"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={!canCustomer}
                        />
                      }
                      label="Customer"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="isSupplier"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={!canSupplier}
                        />
                      }
                      label="Supplier"
                    />
                  )}
                />
              </Stack>
              {errors.isCustomer && (
                <FormHelperText error>{errors.isCustomer.message}</FormHelperText>
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {isCustomer && (
                <TextField
                  label="Credit limit"
                  inputMode="decimal"
                  {...register('creditLimit')}
                  error={!!errors.creditLimit}
                  helperText={errors.creditLimit?.message ?? 'Leave empty for no limit'}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">{format.currencyCode}</InputAdornment>
                      ),
                    },
                  }}
                />
              )}
              <TextField
                label="Payment terms"
                inputMode="numeric"
                {...register('paymentTermsDays')}
                error={!!errors.paymentTermsDays}
                helperText={
                  errors.paymentTermsDays?.message ??
                  (isSupplier && !isCustomer
                    ? 'Days you have to pay'
                    : 'Days until invoices are due')
                }
                slotProps={{
                  input: { endAdornment: <InputAdornment position="end">days</InputAdornment> },
                }}
              />
            </Stack>
            <TextField
              label="Tax number (NTN / STRN)"
              {...register('taxNumber')}
              error={!!errors.taxNumber}
              helperText={errors.taxNumber?.message}
            />
            <TextField
              label="Notes"
              multiline
              minRows={2}
              {...register('notes')}
              error={!!errors.notes}
              helperText={errors.notes?.message ?? 'Only visible to staff'}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={save.isPending}>
            {editing ? 'Save changes' : `Add ${k.singular}`}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
